<script setup>
import Modal from "@/components/modal/Modal.vue";
import Wizard from "@/components/wizard/Wizard.vue";
import {useWizard} from "@/components/wizard";
import WizardStep from "@/components/wizard/WizardStep.vue";
import {useDjangoForm} from "@/utils/forms";
import {ref, useTemplateRef} from "vue";
import * as yup from "yup";
import TicScreen from "@/components/hpo/triage/TicScreen.vue";
import TriageWizardProgress from "@/components/hpo/triage/TriageWizardProgress.vue";
import GenderScreen from "@/components/hpo/triage/GenderScreen.vue";
import IsChildScreen from "@/components/hpo/triage/IsChildScreen.vue";
import PriorityScreen from "@/components/hpo/triage/PriorityScreen.vue";
import {mdiCheckCircle, mdiChevronLeft, mdiClose} from "@mdi/js";
import TriageSummaryScreen from "@/components/hpo/triage/TriageSummaryScreen.vue";
import {useMutation} from "@vue/apollo-composable";
import {HpoTriageMutation} from "@/graphql/mutations/hpo/patient/triageMutation.graphql";
import {pickTriageValues, triageSchema} from "@/components/hpo/triage/schema";
import {useWizardLeaveConfirmation} from "@/components/wizard/composition";
import {pgettext} from "@/utils/translate";
import FormErrorDialog from "@/components/hpo/FormErrorDialog.vue";

yup.addMethod(yup.string, 'fourDigits', function onlyDigits(value) {
  return this.test(`test-four-digits`, gettext('Must contain four digits'), function (value) {
    return /^\d{4}$/.test(value)
  });
});

const props = defineProps({
  hpoId: {
    type: String,
    required: true
  },
  patientList: {
    type: Array,
    default: () => [],
  },
  patientData: {
    type: Object,
  },
});
const emit = defineEmits(['close']);

const errorDialog = useTemplateRef('errorDialog');

function close() {
  emit('close');
}

const launchedAsEdit = !!props.patientData;
const isEditing = ref(launchedAsEdit);

const wizard = useWizard({
  startStepName: launchedAsEdit ? 'summary' : 'tic',
});

const ticStep = wizard.defineStep('tic', {
  next({tic}) {
    for (const patient of props.patientList) {
      if (patient.tic === tic) {
        patientId.value = patient?.id;
        isEditing.value = true;
        ticPrefix.value = patient.ticPrefix;
        ticNumber.value = patient.ticNumber;
        gender.value = patient.gender;
        isChild.value = patient.isChild;
        treatmentPriority.value = patient.treatmentPriority;
        return 'summary';
      }
    }
  },
  enter() {
    if (!launchedAsEdit && patientId.value) {
      patientId.value = null;
      isEditing.value = false;
      resetForm({
        values: {
          ticPrefix: ticPrefix.value,
          ticNumber: ticNumber.value,
        },
      });
    }
  },
});
const genderStep = wizard.defineStep('gender');
const isChildStep = wizard.defineStep('isChild');
const treatmentPriorityStep = wizard.defineStep('treatmentPriority');
const summaryStep = wizard.defineStep('summary');
const confirmationStep = wizard.defineStep('confirmation', {
  enableBack: false,
});
wizard.onEnd(() => {
  close();
});

const schema = yup.object({
  ...triageSchema,
});

const {defineField, handleMutationSubmit, values, isSubmitting, resetForm, meta: formMeta} = useDjangoForm({
  validationSchema: schema,
  initialValues: pickTriageValues(props.patientData),
});

const patientId = ref(props.patientData?.id || null);
const [ticPrefix, ticPrefixProps] = defineField('ticPrefix');
const [ticNumber, ticNumberProps] = defineField('ticNumber');
const [gender, genderProps] = defineField('gender');
const [isChild, isChildProps] = defineField('isChild');
const [treatmentPriority, treatmentPriorityProps] = defineField('treatmentPriority');

function editStep(stepName) {
  wizard.pushFlow([stepName]);
}

const {mutate, loading} = useMutation(HpoTriageMutation);

const onSubmit = handleMutationSubmit(
  (data) => {
    return mutate({
      id: patientId.value,
      hpo: props.hpoId,
      ...data,
    });
  },
  {
    onSuccess(result) {
      wizard.goNext();
    },
    onMutationError(error) {
      errorDialog.value.showMutationError(error);
    },
    onInvalidFormResponse(error) {
      errorDialog.value.showValidationError(error);
    },
  }
);

function resetWizard() {
  // Do not allow form reset when editing a patient.
  if (isEditing.value) {
    return;
  }

  patientId.value = null;
  resetForm();
  wizard.reset();
}

const fieldDefinitions = {
  ticPrefix: {label: pgettext('label', 'TIC Card Letter'), step: ticStep.name},
  ticNumber: {label: pgettext('label', 'TIC'), step: ticStep.name},
  gender: {label: pgettext('label', 'Gender'), step: genderStep.name},
  isChild: {label: pgettext('label', 'Adult or child?'), step: isChildStep.name},
  treatmentPriority: {label: pgettext('label', 'Treatment Priority'), step: treatmentPriorityStep.name},
};

useWizardLeaveConfirmation(
  wizard,
  formMeta,
  pgettext('confirm', 'You have an unsaved patient. Are you sure you want to leave this page?'),
  [
    confirmationStep,
  ],
);
</script>

<template>
  <Modal fullscreen @close="close">
    <template #header>
      <v-card-item>
        <div class="wizard__progress-header">
          <div>
            <v-btn @click="wizard.goBack()"
                   :prepend-icon="mdiChevronLeft"
                   :disabled="!wizard.canGoBack()">
              {{ pgettext('action', 'Back') }}
            </v-btn>
          </div>
          <div class="wizard__progress-bar text-center">
            <TriageWizardProgress v-if="wizard.currentStep.value.name !== confirmationStep.name"
                                  :formData="values"
                                  :wizard="wizard"/>
          </div>
          <div class="text-right">
            <v-btn variant="elevated" rounded="xl" size="x-large" :icon="mdiClose"
                   @click="close"></v-btn>
          </div>
        </div>
      </v-card-item>
    </template>
    <Wizard :wizard="wizard" :max-width="1300">
      <WizardStep :step="ticStep">
        <TicScreen v-model:ticPrefix="ticPrefix"
                   v-model:ticNumber="ticNumber"
                   :hpoId="hpoId"
                   :patientList="patientList"
                   :patientId="patientId"
                   @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="genderStep">
        <GenderScreen v-model="gender" :fieldProps="genderProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="isChildStep">
        <IsChildScreen v-model="isChild" :fieldProps="isChildProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="treatmentPriorityStep">
        <PriorityScreen v-model="treatmentPriority"
                        :title="pgettext('label', 'Treatment Priority')"
                        :fieldProps="treatmentPriorityProps"
                        @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="summaryStep">
        <TriageSummaryScreen :isEditing="isEditing"
                             :personId="patientId"
                             :formData="values"
                             :isSubmitting="isSubmitting"
                             @pushStep="editStep" @submit="onSubmit"/>
      </WizardStep>

      <WizardStep :step="confirmationStep">
        <v-empty-state :icon="mdiCheckCircle" color="green">
          <template #title>
            {{
              interpolate(pgettext('message', 'Patient %(tic)s successfully saved.'), {tic: `${ticPrefix}${ticNumber}`})
            }}
          </template>
          <template #actions>
            <div class="d-flex flex-column ga-6">
              <v-btn @click="wizard.goNext()" size="large" color="primary">
                {{ pgettext('action', 'Close') }}
              </v-btn>
              <v-btn v-if="!isEditing" @click="resetWizard()">
                {{ pgettext('action', 'Add another patient') }}
              </v-btn>
            </div>
          </template>
        </v-empty-state>
      </WizardStep>
    </Wizard>

    <FormErrorDialog ref="errorDialog"
                     :title="pgettext('title', 'Error while saving patient')"
                     :fields="fieldDefinitions"
                     nonFieldErrorStep="tic"
                     @visitStepsWithErrors="wizard.pushFlow"/>
  </Modal>
</template>
